import {
  Badge,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemButtonProps,
  ListItemProps,
  ListItemText,
  Typography,
  styled,
} from '@mui/material';
import { UiCard } from '../../types';

import { CardImage } from '../Card/CardImage';
import { useTranslation } from 'react-i18next';
import { formatCardExpirationDate } from '@razz-team/utils';
import { ReactNode } from 'react';
import { FreezeIcon, TerminateCardIcon, TimeIcon } from '../../icons';
import { Money } from '../Typography/Money';

export function useCardLabel(
  card: UiCard,
  { withHolderName }: { withHolderName?: boolean } = {},
): string {
  const { t } = useTranslation();
  const name = card.name ?? t('Card');
  const holderName = withHolderName && card.cardHolderName ? ` · ${card.cardHolderName}` : '';
  return `${name}${holderName}`;
}

export function useCardDescription(card: UiCard): {
  label: ReactNode;
  icon?: ReactNode;
} {
  const { t } = useTranslation();

  switch (card.status) {
    case 'ACTIVE':
    case 'FROZEN':
      return {
        label: t('•• {{lastFour}}  · Expires on {{expirationDate}}', {
          lastFour: card.lastFour ?? '••••',
          expirationDate: formatCardExpirationDate(card.expirationDate) ?? '··/··',
        }),
        icon: card.status === 'FROZEN' ? <FreezeIcon color="info" /> : undefined,
      };
    case 'CREATED':
      return {
        label: t('Issuing…'),
        icon: <TimeIcon color="secondary" />,
      };
    case 'ISSUED':
      return {
        label: t('Awaiting activation…'),
        icon: <TimeIcon color="secondary" />,
      };
    case 'ACTIVATING':
      return {
        label: t('Activating…'),
        icon: <TimeIcon color="info" />,
      };
    case 'TERMINATED':
      return {
        label: t('Closed'),
        icon: <TerminateCardIcon color="error" />,
      };
    default:
      console.error(`Unknown card status: ${card.status}`);

      return {
        label: t('Unknown'),
      };
  }
}

const TextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
}));

export function CardListItemContent({
  card,
  withHolderName,
}: {
  card: UiCard;
  withHolderName?: boolean;
}) {
  const label = useCardLabel(card, { withHolderName });
  const description = useCardDescription(card);
  const image = (
    <CardImage
      size={40}
      color={card.cardDesign.backgroundColor}
      image={card.cardDesign.backgroundImage}
    />
  );

  return (
    <>
      <ListItemAvatar>
        {description.icon ? (
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={description.icon}
          >
            {image}
          </Badge>
        ) : (
          image
        )}
      </ListItemAvatar>
      <ListItemText
        primary={
          <TextBox>
            <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
              {label}
            </Typography>
          </TextBox>
        }
        secondary={
          <TextBox>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {description.label}
            </Typography>
            {card.balance ? (
              <Money value={card.balance} variant="caption" color="text.secondary" />
            ) : null}
          </TextBox>
        }
        disableTypography
        sx={{ maxWidth: '100%', overflow: 'hidden' }}
      />
    </>
  );
}

export type CardListItemProps = ListItemProps<'li', { card: UiCard; withHolderName?: boolean }>;

export function CardListItem({ card, withHolderName, ...props }: CardListItemProps) {
  return (
    <ListItem {...props}>
      <CardListItemContent card={card} withHolderName={withHolderName} />
    </ListItem>
  );
}

export type CardListItemButtonProps<RootComponent extends React.ElementType = 'div'> =
  ListItemButtonProps<RootComponent, { card: UiCard; withHolderName?: boolean }>;

export function CardListItemButton<RootComponent extends React.ElementType = 'div'>({
  card,
  withHolderName,
  ...props
}: CardListItemButtonProps<RootComponent>) {
  return (
    <ListItemButton {...props}>
      <CardListItemContent card={card} withHolderName={withHolderName} />
    </ListItemButton>
  );
}
